import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function IRSSmallBusinessResourcesPage() {
  const keyResources = [
    {
      title: 'Employer Identification Number (EIN)',
      description: 'Free federal tax ID number for your business',
      icon: 'ri-fingerprint-line',
      details: [
        'Required for hiring employees or opening business bank accounts',
        'Apply online and receive EIN immediately',
        'Free application through IRS website',
        'Can also apply by mail, fax, or phone'
      ]
    },
    {
      title: 'Business Tax Forms',
      description: 'Essential federal tax forms for small businesses',
      icon: 'ri-file-list-3-line',
      details: [
        'Form 1040 Schedule C for sole proprietors',
        'Form 1065 for partnerships',
        'Form 1120 for corporations',
        'Form 941 for quarterly employment taxes'
      ]
    },
    {
      title: 'Small Business Tax Workshops',
      description: 'Free educational programs and webinars',
      icon: 'ri-presentation-line',
      details: [
        'Understanding business taxes workshop',
        'Record keeping and accounting basics',
        'Employment tax obligations',
        'Virtual and in-person sessions available'
      ]
    },
    {
      title: 'Tax Credits &amp; Deductions',
      description: 'Reduce your tax liability with available credits',
      icon: 'ri-money-dollar-circle-line',
      details: [
        'Small Business Health Care Tax Credit',
        'Work Opportunity Tax Credit',
        'Research and Development Credit',
        'Business expense deductions'
      ]
    }
  ];

  const taxCalendar = [
    { date: 'January 31', event: 'Form W-2 and 1099 due to recipients' },
    { date: 'March 15', event: 'S-Corp and Partnership tax returns due' },
    { date: 'April 15', event: 'Individual and C-Corp tax returns due, Q1 estimated tax payment' },
    { date: 'June 15', event: 'Q2 estimated tax payment due' },
    { date: 'September 15', event: 'Q3 estimated tax payment due' },
    { date: 'October 15', event: 'Extended tax return deadline' },
    { date: 'January 15', event: 'Q4 estimated tax payment due' },
    { date: 'Quarterly', event: 'Form 941 employment tax returns (April 30, July 31, Oct 31, Jan 31)' }
  ];

  const businessStructures = [
    {
      type: 'Sole Proprietorship',
      taxForm: 'Schedule C (Form 1040)',
      description: 'Report business income on personal tax return'
    },
    {
      type: 'Partnership',
      taxForm: 'Form 1065',
      description: 'Information return; partners report on Schedule K-1'
    },
    {
      type: 'LLC',
      taxForm: 'Varies',
      description: 'Taxed as sole proprietor, partnership, or corporation'
    },
    {
      type: 'S Corporation',
      taxForm: 'Form 1120-S',
      description: 'Pass-through taxation; shareholders report on K-1'
    },
    {
      type: 'C Corporation',
      taxForm: 'Form 1120',
      description: 'Corporate tax return; separate from owners'
    }
  ];

  const onlineTools = [
    'IRS.gov - Official IRS website with forms and publications',
    'IRS2Go Mobile App - Check refund status and make payments',
    'Interactive Tax Assistant - Get answers to tax questions',
    'Tax Withholding Estimator - Calculate proper withholding',
    'Online Payment Agreement - Set up installment plans',
    'Get Transcript - Access tax return transcripts online'
  ];

  return (
    <>
      <SEOHead
        title="IRS Small Business Resources & Federal Tax Guide"
        description="Comprehensive guide to IRS small business resources and federal tax requirements. Learn about EIN registration, business tax deductions, quarterly taxes, and IRS filing requirements for small businesses."
        canonical="/regulations/irs-small-business-resources"
        keywords="IRS small business, EIN registration, federal business taxes, IRS tax deductions, quarterly taxes, business tax filing"
        ogImage="https://public.readdy.ai/ai/img_res/48bbf90c611b23a6b16f10de22a113b5.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link to="/regulations" className="text-[#D2691E] hover:underline text-sm font-medium">
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "IRS Small Business Resources",
                    "item": "https://elpasotexasbusiness.com/regulations/irs-small-business-resources"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl flex items-center justify-center">
                  <i className="ri-bank-line text-3xl text-white"></i>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900">IRS Small Business Resources</h1>
                  <p className="text-lg text-gray-600 mt-1">Federal tax information and EIN registration</p>
                </div>
              </div>
              <p className="text-base text-gray-700 leading-relaxed max-w-4xl">
                The Internal Revenue Service provides comprehensive resources for small business owners to understand 
                federal tax obligations, obtain an Employer Identification Number, access tax forms, and stay compliant 
                with federal tax laws. These free resources help you navigate the complexities of business taxation.
              </p>
            </div>

            {/* Key Resources Grid */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Essential IRS Resources</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {keyResources.map((resource, index) => (
                  <div key={index} className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg transition-all">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="w-12 h-12 bg-[#1B3A3A] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${resource.icon} text-xl text-white`}></i>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{resource.description}</p>
                      </div>
                    </div>
                    <ul className="space-y-2 ml-16">
                      {resource.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <i className="ri-checkbox-circle-fill text-[#2C5F4F] text-sm mt-1 flex-shrink-0"></i>
                          <span className="text-sm text-gray-700 leading-relaxed">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* EIN Application Section */}
            <div className="mb-16 bg-gradient-to-br from-[#FFE8D6] to-[#FFD4B8] rounded-3xl p-10">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 bg-[#1B3A3A] rounded-xl flex items-center justify-center">
                  <i className="ri-shield-check-line text-xl text-white"></i>
                </div>
                <h2 className="text-3xl font-bold text-gray-900">How to Apply for an EIN</h2>
              </div>
              <p className="text-base text-gray-700 leading-relaxed mb-6">
                An Employer Identification Number (EIN) is a unique nine-digit number assigned by the IRS to identify 
                your business for tax purposes. It's free and easy to obtain.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Application Methods</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start space-x-3">
                      <i className="ri-computer-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Online (Recommended)</p>
                        <p className="text-sm text-gray-600">Instant EIN - available Monday-Friday, 7am-10pm ET</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-phone-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">By Phone</p>
                        <p className="text-sm text-gray-600">Call 1-800-829-4933 (7am-7pm local time)</p>
                      </div>
                    </li>
                    <li className="flex items-start space-x-3">
                      <i className="ri-mail-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">By Mail or Fax</p>
                        <p className="text-sm text-gray-600">Submit Form SS-4 (4-5 weeks processing)</p>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="bg-white rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Required Information</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#2C5F4F] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">Legal business name and structure</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#2C5F4F] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">Responsible party's SSN or ITIN</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#2C5F4F] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">Business address and mailing address</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#2C5F4F] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">Reason for applying (new business, hiring, etc.)</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#2C5F4F] text-sm mt-1"></i>
                      <span className="text-sm text-gray-700">Principal business activity</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-[#1B3A3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#2C5F4F] transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>Apply for EIN Online</span>
                  <i className="ri-external-link-line"></i>
                </a>
                <a
                  href="https://www.irs.gov/pub/irs-pdf/fss4.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-white text-[#1B3A3A] px-6 py-3 rounded-xl font-semibold border-2 border-[#1B3A3A] hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <span>Download Form SS-4</span>
                  <i className="ri-download-line"></i>
                </a>
              </div>
            </div>

            {/* Business Structure Tax Forms */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Tax Forms by Business Structure</h2>
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-[#1B3A3A] to-[#2C5F4F]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Business Structure</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Primary Tax Form</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Tax Treatment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {businessStructures.map((structure, index) => (
                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">{structure.type}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{structure.taxForm}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{structure.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Tax Calendar */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Small Business Tax Calendar</h2>
              <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-8">
                <div className="space-y-4">
                  {taxCalendar.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 pb-4 border-b border-gray-200 last:border-0 last:pb-0">
                      <div className="w-32 flex-shrink-0">
                        <div className="bg-[#1B3A3A] text-white text-center py-2 px-3 rounded-lg">
                          <span className="text-sm font-bold whitespace-nowrap">{item.date}</span>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 leading-relaxed">{item.event}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600 leading-relaxed">
                    <strong>Important:</strong> When a deadline falls on a Saturday, Sunday, or legal holiday, 
                    the due date is the next business day. Always verify current year deadlines on IRS.gov.
                  </p>
                </div>
              </div>
            </div>

            {/* Online Tools */}
            <div className="mb-16 bg-gradient-to-br from-[#FFE8D6] to-[#FFD4B8] rounded-3xl p-10">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">IRS Online Tools &amp; Services</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {onlineTools.map((tool, index) => (
                  <div key={index} className="flex items-center space-x-3 bg-white/60 rounded-xl p-4">
                    <i className="ri-tools-line text-[#2C5F4F] text-xl"></i>
                    <span className="text-sm font-medium text-gray-800">{tool}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-16 bg-white border border-gray-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">IRS Contact Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Business Tax Assistance</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <i className="ri-phone-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Business &amp; Specialty Tax Line</p>
                        <p className="text-sm text-gray-700">1-800-829-4933</p>
                        <p className="text-sm text-gray-600">Monday - Friday, 7:00 AM - 7:00 PM local time</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-customer-service-line text-[#2C5F4F] text-lg mt-0.5"></i>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">General Tax Questions</p>
                        <p className="text-sm text-gray-700">1-800-829-1040</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Official Website</h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    Access forms, publications, tax law information, and online tools at the official IRS website.
                  </p>
                  <a
                    href="https://www.irs.gov/businesses/small-businesses-self-employed"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 text-[#1B3A3A] font-semibold hover:text-[#2C5F4F] transition-colors cursor-pointer"
                  >
                    <span>IRS Small Business Resources</span>
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-10 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">Questions About Federal Tax Requirements?</h2>
              <p className="text-base text-white/90 leading-relaxed mb-6 max-w-2xl mx-auto">
                We can help you understand your federal tax obligations and connect you with resources 
                to keep your business compliant with IRS requirements.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-2 bg-[#FFE8D6] text-[#1B3A3A] px-8 py-4 rounded-xl font-bold hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
              >
                <span>Contact Us</span>
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}